// Shared Teams SDK initialization state.
// teamsContext.tsx drives the actual app.initialize() call (inside a React useEffect,
// which runs after the DOM is ready and the Teams Desktop bridge is injected).
// authConfig.ts awaits the result via waitForTeamsInit() instead of calling
// app.initialize() on its own, which would fire too early and fail with
// "No Parent window found" on Teams Desktop.

import { app, authentication } from "@microsoft/teams-js";

let resolveInit: ((value: boolean) => void) | null = null;
let initSettled = false;
let initResult = false;

// Promise that authConfig.ts can await. Resolves once teamsContext.tsx reports the outcome.
const initPromise = new Promise<boolean>(resolve => {
    resolveInit = resolve;
});

/**
 * Called by teamsContext.tsx after app.initialize() succeeds or fails.
 * Must be called exactly once per page load.
 */
export function reportTeamsInitResult(succeeded: boolean): void {
    if (initSettled) return;
    initSettled = true;
    initResult = succeeded;
    if (resolveInit) {
        resolveInit(succeeded);
        resolveInit = null;
    }
}

/**
 * Called by authConfig.ts to wait for the Teams SDK init result.
 * Returns true if running inside Teams, false otherwise.
 * If teamsContext.tsx hasn't reported yet, this waits up to timeoutMs before giving up.
 */
export async function waitForTeamsInit(timeoutMs = 5000): Promise<boolean> {
    if (initSettled) return initResult;

    const timeout = new Promise<boolean>(resolve => {
        setTimeout(() => {
            resolve(false);
        }, timeoutMs);
    });

    const result = await Promise.race([initPromise, timeout]);
    return result;
}

/**
 * Get an SSO token from the Teams SDK. Only works after Teams init succeeds.
 */
export async function getTeamsAuthToken(): Promise<string | null> {
    try {
        const token = await authentication.getAuthToken();
        return token || null;
    } catch (err) {
        return null;
    }
}

export { app };
